import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import {
  Bell,
  Building2,
  Database,
  FileBadge,
  Hourglass,
  ListChecks,
  Mail,
  PenTool,
  Scale,
  Send,
  UserRound,
  Users,
  type LucideIcon,
} from "lucide-react"
import type { ComponentType } from "react"
import { NotificationsPage } from "./pages/account/NotificationsPage"
import { ProfilePage } from "./pages/account/ProfilePage"
import { SignaturesPage } from "./pages/account/SignaturesPage"
import { UsagePage } from "./pages/account/UsagePage"
import { ConformityDeclarationsPage } from "./pages/material-compliance/ConformityDeclarationsPage"
import { MasterDataPage } from "./pages/material-compliance/MasterDataPage"
import { RegulationsPage } from "./pages/material-compliance/RegulationsPage"
import { SupplierInquiriesPage } from "./pages/material-compliance/supplier-inquiries/SupplierInquiriesPage"
import { EmailSendingPage } from "./pages/organization/EmailSendingPage"
import { GeneralPage } from "./pages/organization/GeneralPage"
import { MembersPage } from "./pages/organization/MembersPage"
import { SetupChecklistPage } from "./pages/setup/SetupChecklistPage"
import { SETTINGS_TITLE_KEYS, type SettingsSectionId } from "./paths"

/** Who a change on the page affects: just you, everyone, or one module.
 *  `setup` is the checklist above them, across all three: no label. */
export type SettingsGroupId =
  | "setup"
  | "account"
  | "organization"
  | "materialCompliance"

export const SETTINGS_GROUPS: {
  id: SettingsGroupId
  labelKey?: TranslationKeys
}[] = [
  { id: "setup" },
  { id: "account", labelKey: "SETTINGS/GROUP/ACCOUNT" },
  { id: "organization", labelKey: "SETTINGS/GROUP/ORGANIZATION" },
  { id: "materialCompliance", labelKey: "SETTINGS/GROUP/MATERIAL_COMPLIANCE" },
]

export interface SettingsSectionDef {
  id: SettingsSectionId
  group: SettingsGroupId
  icon: LucideIcon
  titleKey: TranslationKeys
  Page: ComponentType
  /** The page has tabs, each its own URL under the page's. */
  hasTabs?: boolean
}

/** Every settings page, in nav order. Which ones a user gets:
 *  `useSettingsVisibility`. */
export const SETTINGS_SECTIONS: SettingsSectionDef[] = [
  {
    id: "setup",
    group: "setup",
    icon: ListChecks,
    titleKey: SETTINGS_TITLE_KEYS.setup,
    Page: SetupChecklistPage,
  },
  {
    id: "profile",
    group: "account",
    icon: UserRound,
    titleKey: SETTINGS_TITLE_KEYS.profile,
    Page: ProfilePage,
  },
  {
    id: "notifications",
    group: "account",
    icon: Bell,
    titleKey: SETTINGS_TITLE_KEYS.notifications,
    Page: NotificationsPage,
  },
  {
    id: "signatures",
    group: "account",
    icon: PenTool,
    titleKey: SETTINGS_TITLE_KEYS.signatures,
    Page: SignaturesPage,
  },
  {
    id: "usage",
    group: "account",
    icon: Hourglass,
    titleKey: SETTINGS_TITLE_KEYS.usage,
    Page: UsagePage,
  },
  {
    id: "general",
    group: "organization",
    icon: Building2,
    titleKey: SETTINGS_TITLE_KEYS.general,
    Page: GeneralPage,
  },
  {
    id: "emailSending",
    group: "organization",
    icon: Mail,
    titleKey: SETTINGS_TITLE_KEYS.emailSending,
    Page: EmailSendingPage,
  },
  {
    id: "members",
    group: "organization",
    icon: Users,
    titleKey: SETTINGS_TITLE_KEYS.members,
    Page: MembersPage,
  },
  {
    id: "regulations",
    group: "materialCompliance",
    icon: Scale,
    titleKey: SETTINGS_TITLE_KEYS.regulations,
    Page: RegulationsPage,
  },
  {
    id: "masterData",
    group: "materialCompliance",
    icon: Database,
    titleKey: SETTINGS_TITLE_KEYS.masterData,
    Page: MasterDataPage,
  },
  {
    id: "conformityDeclarations",
    group: "materialCompliance",
    icon: FileBadge,
    titleKey: SETTINGS_TITLE_KEYS.conformityDeclarations,
    Page: ConformityDeclarationsPage,
  },
  {
    id: "supplierInquiries",
    group: "materialCompliance",
    icon: Send,
    titleKey: SETTINGS_TITLE_KEYS.supplierInquiries,
    Page: SupplierInquiriesPage,
    hasTabs: true,
  },
]
